/**
 * 방 관리 API 클라이언트 — §9.2.
 *
 * 배경: 회의가 끝나 브라우저를 닫아도 방은 살아 있다(§9 유예 — 설계대로다).
 * 없던 것은 **그걸 볼 방법과 치울 손잡이**였다.
 *
 * 인가 판정은 서버가 한다. 관리자는 전체, 그 외에는 자기가 연 방만 —
 * 화면이 남의 방 버튼을 숨기는 것과 **별개의 통제**다.
 *
 * **자격은 Bearer 권한 토큰이다(§5.2)**: 이 파일의 경로는 전부 방을 소유한
 * **stream 서비스**(/api/rooms*, /api/admin/rooms*)로 가고, 분리 형상에서
 * stream 에는 세션이 없다(세션은 auth 메모리 전용). auth 가 로그인 세션으로
 * 발급한 account/admin 토큰이 신원(sub)·권한(cap)을 운반한다. 단일
 * 프로세스에서는 세션 쿠키만으로도 동작하므로 발급 실패는 요청을 막지
 * 않는다(서버 판정이 정본 — api.ts createRoom 과 같은 규율).
 */

import { API_BASE } from './config'
import { ApiError } from './api'
import { accountAuthHeaders } from './token'
import type { ExportFormat } from './export'
import { parseFilename, fallbackFilename, saveBlob } from './export'

/**
 * 방 1개의 관리용 요약 — **비밀 없음**.
 *
 * 서버가 `host_key`·`view_key`·`passcode` 를 담지 않는다. 이 목록은 무엇을
 * 닫을지 고르는 곳이지 자격을 얻는 곳이 아니다.
 */
/**
 * 이 방의 **음성 녹음** 상태 — S10.
 *
 * `RoomSummary.record`(자막·대화 텍스트 기록)와 **다른 축**이다. 저건
 * 회의록이고 이건 원본 음성이다. 이름이 비슷해 헷갈리기 쉬운 자리라
 * 화면 문구도 "대화 기록" / "음성 녹음" 으로 갈라 부른다.
 */
export interface RoomDumpState {
  /** 지금 이 방의 음성이 디스크에 적히고 있는가 */
  on: boolean
  /**
   * 무엇이 켰는가. 이 값이 **끌 수 있는지**를 가른다:
   *   · `'admin'` — 화면에서 껐다 켰다 한다.
   *   · `'env'`   — 배포 env 전역 녹음. 화면에서 끌 수 없다(운영자가 .env 를
   *     지우고 재기동해야 한다 — docs/DUMP_OPS.md). 끌 수 없는 스위치를
   *     끌 수 있는 것처럼 그리면 "눌렀는데 계속 녹음되는" 최악이 된다.
   *   · `''`      — 꺼짐.
   */
  source: 'admin' | 'env' | ''
  /** 켠 관리자(감사 로그와 같은 값). env 전역이면 빈 문자열 */
  actor: string
  /** 켠 시각(epoch 초). 0 = 없음 */
  since: number
  /** 이 프로세스가 이 방에 쓴 바이트 */
  bytes: number
  /** 방별 상한에 닿아 저장이 멈췄는가 — **꺼진 것과 같지 않다** */
  stopped_at_cap: boolean
}

export interface RoomSummary {
  room: string
  /** 표에 쓰는 짧은 ID(가독성 — 비밀이라서가 아니다) */
  short: string
  /** S28 회의 이름. 구 서버는 안 준다 */
  name?: string
  /** S28 회의 주제. 구 서버는 안 준다 */
  topic?: string
  owner: string
  state: 'open' | 'live' | 'suspended' | 'closed'
  created_at: number
  last_activity: number
  /** 서버가 계산한 무활동 경과(초) — 클라이언트 시계를 믿지 않는다 */
  idle_s: number
  /** 호스트 소켓이 지금 붙어 있는가. "브라우저를 닫았다" = false */
  host_connected: boolean
  viewers: number
  src: string
  dst: string
  /** 자막·대화 **텍스트** 기록(§11) — 음성 녹음이 아니다(`dump` 참조) */
  record: boolean
  retention_days: number
  captions: number
  chats: number
  last_seq: number
  public_entry: boolean
  /** S10 음성 녹음 상태. 구 서버는 안 보낸다 — 화면이 없음을 견뎌야 한다 */
  dump?: RoomDumpState
}

const MESSAGES: Record<string, string> = {
  forbidden: '이 방을 닫을 권한이 없습니다.',
  not_found: '방을 찾을 수 없습니다.',
  unauthenticated: '로그인이 필요합니다.',
  // S10 — 배포가 녹음을 아예 금지한 형상(env 가 경로를 주지 않았다).
  // 관리자 권한으로도 뒤집히지 않으므로 "권한이 없다" 로 말하면 안 된다 —
  // 다른 계정으로 다시 시도하게 만든다.
  dump_disabled: '이 서버는 음성 녹음이 꺼져 있습니다. 서버 설정(TRANS_ADMIN_DUMP_DIR)이 필요합니다.',
  room_closed: '이미 종료된 회의실입니다.',
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(await accountAuthHeaders()),
      ...(init?.headers ?? {}),
    },
  })
  const text = await res.text()
  let body: unknown = {}
  try {
    body = text ? JSON.parse(text) : {}
  } catch {
    body = {}
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown }).detail
    const code = typeof detail === 'string' ? detail : 'unknown'
    throw new ApiError(res.status, code, MESSAGES[code] ?? '요청이 실패했습니다.')
  }
  return body as T
}

/** 전체 방(관리자). */
export function listAllRooms(): Promise<{
  rooms: RoomSummary[]
  idle_threshold_s: number
}> {
  return req('/admin/rooms')
}

/** **내가 연 회의실** — 로그인 계정이면 누구나(§9.2 출발점). */
export function listMyRooms(): Promise<{ rooms: RoomSummary[] }> {
  return req('/rooms/mine')
}

/**
 * 호스트 재입장 응답(DEF-131) — `POST /api/rooms/{room}/token`.
 *
 * 개설 응답과 같은 자격을 다시 꾸리는 데 쓴다. **소유자만** 받는다(서버
 * `owns` — 관리자 분기 없음). `view_key` 는 공개 방일 때만 온다.
 */
export interface HostResumeResponse {
  ok: true
  room: string
  role: 'host'
  token: string
  token_ttl_s: number
  passcode: string
  view_key?: string
  src: string
  dst: string
  bidirectional: boolean
  name: string
  topic: string
  public_entry: boolean
}

/** 내 방에 **호스트로 돌아가기** — 새 host 토큰과 개설 때의 자격 필드. */
export function resumeHost(room: string): Promise<HostResumeResponse> {
  return req(`/rooms/${encodeURIComponent(room)}/token`, { method: 'POST' })
}

/**
 * 강제 종료(파기) — 관리자는 전부, 그 외에는 자기 방만.
 *
 * **계정 세션으로** 판정한다(방 키가 아니라). 브라우저를 닫아 host_key 를
 * 잃은 사람이 자기 방을 치우려는 상황이 바로 이 기능의 용례다.
 */
export function forceCloseRoom(room: string): Promise<{ ok: true }> {
  return req(`/rooms/${encodeURIComponent(room)}/force-close`, { method: 'POST' })
}

/**
 * 이 회의의 **음성 녹음**을 켜거나 끈다(관리자 전용 — S10).
 *
 * 서버가 켬·끔을 **둘 다 감사에 남긴다**(§11.5). 끈 사실이 곧 "녹음 창을
 * 닫았다" 는 증거라서다 — 켬만 남으면 그 창은 사후에 닫힌 적이 없는 것으로
 * 읽힌다.
 *
 * 인가는 서버의 `_require_room_admin` 하나다. 방 소유자에게는 **열려 있지
 * 않다**: 회의를 연 사람이 상대측 동의 없이 관세 협상을 녹음하는 손잡이가
 * 되면 안 된다(server/main.py S10 절).
 */
export function setRoomDump(
  room: string,
  on: boolean,
): Promise<{ ok: true; room: string; dump: RoomDumpState }> {
  return req(`/admin/rooms/${encodeURIComponent(room)}/dump`, {
    method: 'POST',
    body: JSON.stringify({ on }),
  })
}

/** 유휴 방 미리보기 — 무엇이 닫힐지 먼저 보여 준다. */
export function previewIdleRooms(
  thresholdS: number,
): Promise<{ rooms: RoomSummary[]; threshold_s: number }> {
  return req(`/admin/rooms/idle?threshold_s=${Math.max(0, Math.floor(thresholdS))}`)
}

/** 유휴 방 일괄 종료(관리자). */
export function cleanupIdleRooms(
  thresholdS: number,
): Promise<{ ok: true; closed: number; rooms: string[] }> {
  return req('/admin/rooms/cleanup', {
    method: 'POST',
    body: JSON.stringify({ threshold_s: Math.max(0, Math.floor(thresholdS)) }),
  })
}

/**
 * 방 회의록 백업(관리자) — 파기 전에 내용을 건진다.
 *
 * 열람은 서버에서 **감사 로그에 남는다**(§11.5) — 참가하지 않은 회의를 읽는
 * 행위이기 때문이다. 화면이 그 사실을 숨기지 않는다.
 */
export async function backupRoom(
  room: string,
  fmt: ExportFormat = 'txt',
): Promise<{ filename: string; count: number; partial: boolean }> {
  const res = await fetch(
    `${API_BASE}/admin/rooms/${encodeURIComponent(room)}/export?fmt=${fmt}`,
    { credentials: 'same-origin', headers: await accountAuthHeaders() },
  )
  if (!res.ok) {
    let code = 'unknown'
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') code = body.detail
    } catch {
      /* 본문이 없거나 JSON 이 아니면 상태만 남긴다 */
    }
    throw new ApiError(
      res.status,
      code,
      res.status === 403 ? '백업 권한이 없습니다.' : '백업을 받지 못했습니다.',
    )
  }
  const blob = await res.blob()
  const filename =
    parseFilename(res.headers.get('Content-Disposition')) || fallbackFilename(room, fmt)
  saveBlob(blob, filename)
  return {
    filename,
    count: Number(res.headers.get('X-Transcript-Count') ?? 0),
    partial: res.headers.get('X-Transcript-Partial') === '1',
  }
}

/**
 * 여러 방을 **차례로** 백업한다.
 *
 * zip 을 만들지 않는 이유: 브라우저에서 zip 을 만들려면 압축 라이브러리를
 * 번들에 넣어야 하는데, 이 앱은 CSP·번들 크기를 이유로 외부 의존을 늘리지
 * 않는다. 순차 저장은 의존이 0 이고 실패해도 **어느 방에서 멈췄는지** 가
 * 분명하다(zip 은 하나가 깨지면 통째로 못 연다).
 *
 * 브라우저의 연속 다운로드 차단을 피하려고 사이에 짧은 간격을 둔다.
 */
export async function backupRooms(
  rooms: string[],
  fmt: ExportFormat = 'txt',
  onProgress?: (done: number, total: number, room: string) => void,
): Promise<{ ok: string[]; failed: string[] }> {
  const ok: string[] = []
  const failed: string[] = []
  for (let i = 0; i < rooms.length; i += 1) {
    const room = rooms[i] as string
    try {
      await backupRoom(room, fmt)
      ok.push(room)
    } catch {
      // 하나가 실패해도 나머지는 계속 받는다 — 일괄 백업의 목적은
      // "최대한 건지는 것" 이다.
      failed.push(room)
    }
    onProgress?.(i + 1, rooms.length, room)
    if (i < rooms.length - 1) {
      await new Promise((r) => setTimeout(r, 250))
    }
  }
  return { ok, failed }
}

/**
 * 관리자가 남의 방에 뷰어로 참가한다(S22) — 감사 로그에 남는다.
 *
 * 일반 뷰어 입장(패스코드)과 응답 모양이 다르다: `passcode`·`host_key`·
 * `anonymous` 가 없다 — 관리자는 패스코드 없이 신원으로 들어간다.
 */
export interface AdminJoinResponse {
  ok: true
  room: string
  /** 구 view_key — 서버가 더는 싣지 않는다(자격은 `token`). 구 서버 호환. */
  view_key?: string
  src: string
  dst: string
  bidirectional?: boolean
  token?: string
  token_ttl_s?: number
}

export function adminJoinRoom(room: string): Promise<AdminJoinResponse> {
  return req(`/admin/rooms/${encodeURIComponent(room)}/join`, { method: 'POST' })
}

/* ── 표시 도우미 ────────────────────────────────────────────────────────── */

export const ROOM_STATE_LABELS: Record<RoomSummary['state'], string> = {
  open: '대기',
  live: '진행 중',
  suspended: '호스트 끊김',
  closed: '종료됨',
}

/** 경과 초 → 사람이 읽는 길이. 서버가 준 값을 그대로 쓴다(시계 불일치 방지). */
export function formatIdle(sec: number): string {
  if (sec < 60) return '방금'
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}분 전`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 ${m % 60}분 전`
  return `${Math.floor(h / 24)}일 전`
}
